import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { STATS, SERVICES, COURSES, TESTIMONIALS, REELS } from "@/components/birthlife/data";

const DEFAULT_SECTIONS = [
  { key: "about", order: 1, visible: true },
  { key: "podcasts", order: 2, visible: true },
  { key: "marquee", order: 3, visible: true },
  { key: "services", order: 4, visible: true },
  { key: "courses", order: 5, visible: true },
  { key: "stories", order: 6, visible: true },
  { key: "instagram", order: 7, visible: true },
  { key: "corporate", order: 8, visible: true },
];

const FALLBACK = {
  stats: STATS,
  services: SERVICES,
  courses: COURSES,
  stories: TESTIMONIALS,
  reels: REELS,
  podcasts: [],
  sections: DEFAULT_SECTIONS,
  pages: {},
  settings: {
    whatsapp_number: "+919717781781",
    corporate_email: "birthlifeindia@gmail.com",
    email: "birthlifeindia@gmail.com",
    phone_display: "+91 97177 81781",
    instagram_handle: "@thebirth.life",
    instagram_url: "https://www.instagram.com/thebirth.life",
    location_text: "CK Birla Hospital rounds · Gurgaon, Haryana, India",
  },
};

const SiteContext = createContext({ content: FALLBACK, refresh: () => {} });

export const useSite = () => useContext(SiteContext);

export function SiteProvider({ children }) {
  const [content, setContent] = useState(FALLBACK);

  const load = () =>
    api
      .get("/site")
      .then(async (r) => {
        const base = { ...FALLBACK, ...r.data };
        try {
          const st = await api.get("/propract/status");
          if (st.data?.configured) {
            const [svc, ev] = await Promise.allSettled([
              api.get("/propract/services"),
              api.get("/propract/events"),
            ]);
            const failed = svc.status === "rejected" && ev.status === "rejected";
            base.propract = {
              configured: true,
              services: svc.status === "fulfilled" ? svc.value.data?.services || svc.value.data || [] : [],
              events: ev.status === "fulfilled" ? ev.value.data?.events || ev.value.data || [] : [],
              error: failed ? "Could not load catalog — check slug in Admin → Integrations" : null,
            };
          } else {
            base.propract = { configured: false, services: [], events: [] };
          }
        } catch {
          base.propract = { configured: false, services: [], events: [] };
        }
        setContent(base);
      })
      .catch(() => setContent(FALLBACK));

  useEffect(() => {
    load();
  }, []);

  return <SiteContext.Provider value={{ content, refresh: load }}>{children}</SiteContext.Provider>;
}

const norm = (s) => (s || "").toLowerCase().trim();

const mapPpService = (s) => ({
  service_id: s.service_id,
  pp_id: s.service_id,
  title: s.name || s.title || "",
  desc: s.short_description || s.description || "",
  duration: s.duration ? `${s.duration} min` : s.duration_minutes ? `${s.duration_minutes} min` : "",
  price: s.price != null ? `₹${Number(s.price).toLocaleString("en-IN")}` : s.price_display || "",
  mode: s.mode === "in_person" ? "In-clinic" : s.mode === "online" ? "Online" : s.mode === "hybrid" ? "In-clinic & Online" : s.mode || "",
  mode_raw: s.mode,
  book_url: s.book_url || "",
  allow_online_payment: !!s.allow_online_payment,
  allow_direct_payment: !!s.allow_direct_payment,
  allow_pay_in_person: !!s.allow_pay_in_person,
  direct_payment_details: s.direct_payment_details || null,
});

const mapPpEvent = (e) => ({
  event_id: e.event_id,
  pp_id: e.event_id,
  title: e.title || e.name || "",
  tagline: (e.short_description || "").trim(),
  desc: e.description || "",
  modules: e.module_count || e.modules_count || e.sessions || "",
  hours: e.duration ? `${e.duration} min` : e.hours || "",
  price: e.price != null ? `₹${Number(e.price).toLocaleString("en-IN")}` : e.price_display || "",
  book_url: e.book_url || e.enroll_url || "",
  thumbnail: e.cover_image || e.thumbnail || e.image_url || "",
  pricing_tiers: e.pricing_tiers || [],
  offering_type: e.offering_type || "",
  delivery_method: e.delivery_method || "",
  dates: e.dates || [],
  event_mode: e.mode || "",
  location_address: e.location_address || "",
  rolling_enrollment: !!e.rolling_enrollment,
  kind: e.delivery_method === "prerecorded" || e.offering_type === "self_paced" ? "course" : "event",
});

export function useCatalog() {
  const { content } = useSite();
  const pp = content.propract || {};
  const cmsServices = content.services?.length ? content.services : SERVICES;
  const cmsCourses = content.courses?.length ? content.courses : COURSES;

  let services = cmsServices;
  if (pp.configured && pp.services?.length) {
    const byId = Object.fromEntries(cmsServices.map((s) => [s.pp_id, s]).filter(([k]) => k));
    const byTitle = Object.fromEntries(cmsServices.map((s) => [norm(s.title), s]));
    services = pp.services.map((raw) => {
      const mapped = mapPpService(raw);
      const cms = byId[mapped.pp_id] || byTitle[norm(mapped.title)] || {};
      return { ...mapped, why: cms.why || "", who: cms.who || "" };
    });
  }

  let courses = cmsCourses;
  if (pp.configured && pp.events?.length) {
    const byId = Object.fromEntries(cmsCourses.map((c) => [c.pp_id, c]).filter(([k]) => k));
    const byTitle = Object.fromEntries(cmsCourses.map((c) => [norm(c.title), c]));
    courses = pp.events.map((raw) => {
      const mapped = mapPpEvent(raw);
      const cms = byId[mapped.pp_id] || byTitle[norm(mapped.title)] || {};
      return { ...mapped, why: cms.why || "", who: cms.who || "", thumbnail: cms.thumbnail || mapped.thumbnail, kind: cms.kind || mapped.kind };
    });
  }

  const liveEvents = courses.filter((c) => c.kind === "event");
  const selfPacedCourses = courses.filter((c) => c.kind !== "event");

  return { services, courses: selfPacedCourses, liveEvents, propractLive: !!(pp.configured && pp.services?.length), propractError: pp.error };
}
